import { isPast, isToday } from 'date-fns';
import { useLocation } from 'wouter';
import type { CheckedState } from '@radix-ui/react-checkbox';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { Pencil, User } from 'lucide-react';
import { formatClaimReference, isTaskClosed, type TaskClaimSummary } from '@/lib/task-filters';
import type { ClaimTask, TaskPriority } from '@/api/generated/client';
import { Button } from '@/components/ui/button';

interface TaskListItemProps {
  task: ClaimTask;
  claim?: TaskClaimSummary;
  onTaskComplete?: (taskId: number, claimId: number) => void;
  onTaskEdit?: (task: ClaimTask) => void;
  /**
    * 'stacked' (default): assignee appears beside priority below the claim reference.
    * Used by the calendar view's day task list (TaskList.tsx).
   *
    * 'inline': assignee appears beside the claim reference; priority stays below it.
    * Used by NoDueDateTasksCard.
   */
  badgeLayout?: 'stacked' | 'inline';
}

const priorityConfig: Record<TaskPriority, { label: string; className: string }> = {
  urgent: { label: 'Urgent', className: 'bg-red-500 hover:bg-red-600' },
  high: { label: 'High', className: 'bg-orange-500 hover:bg-orange-600' },
  medium: { label: 'Medium', className: 'bg-yellow-500 hover:bg-yellow-600' },
  low: { label: 'Low', className: 'bg-blue-500 hover:bg-blue-600' }
};

export function TaskListItem({ task, claim, onTaskComplete, onTaskEdit, badgeLayout = 'stacked' }: TaskListItemProps) {
  const [, setLocation] = useLocation();
  const canWrite = claim?.canWrite ?? false;
  // A cancelled task is "closed" like a completed one — it shouldn't read as
  // overdue/active, and its checkbox shouldn't be actionable. The checkbox's
  // checked state below still only reflects true completion, not cancellation.
  const isClosed = isTaskClosed(task);

  const dueDate = task.dueDate ? new Date(task.dueDate) : null;
  const isOverdue = dueDate && isPast(dueDate) && !isToday(dueDate) && !isClosed;

  const handleCheckboxChange = (checked: CheckedState) => {
    if (checked === true && !isClosed && onTaskComplete) {
      onTaskComplete(task.id, task.claimId);
    }
  };

  return (
    <div
      className={cn(
        'relative p-3 rounded-lg border transition-all',
        'hover:shadow-md hover:border-primary/50',
        isOverdue && 'border-red-500/50 bg-red-500/5'
      )}
    >
      <div className="flex gap-3">
        {/* Checkbox for completion */}
        <div className="pt-0.5">
          <Checkbox
            checked={task.status === 'completed'}
            onCheckedChange={handleCheckboxChange}
            disabled={isClosed || !canWrite}
            className="h-5 w-5"
          />
        </div>

        <div className="relative min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setLocation(`/claims/${task.claimId}`)}
            className="w-full text-left focus:outline-none"
          >
            <div className="space-y-1">
              <h3 className={cn(
                'font-medium line-clamp-3',
                isClosed && 'text-muted-foreground'
              )}>
                {task.title}
              </h3>

              {(claim || (badgeLayout === 'inline' && task.assignedToName)) && (
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <div className="flex min-w-0 items-center gap-2">
                    {claim && <span className="truncate">{formatClaimReference(claim)}</span>}
                    {claim && !canWrite && (
                      <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0 text-amber-700 border-amber-300 bg-amber-50">
                        View only
                      </Badge>
                    )}
                  </div>
                  {badgeLayout === 'inline' && task.assignedToName && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 gap-1 shrink-0 text-blue-600 border-blue-200 bg-blue-50">
                      <User className="h-3 w-3" />
                      {task.assignedToName}
                    </Badge>
                  )}
                </div>
              )}

              <div
                data-testid="task-priority-row"
                className={cn('flex items-center justify-end gap-2 text-xs', canWrite && onTaskEdit && 'pr-7')}
              >
                {badgeLayout === 'stacked' && task.assignedToName && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1 gap-1 shrink-0 text-blue-600 border-blue-200 bg-blue-50">
                    <User className="h-3 w-3" />
                    {task.assignedToName}
                  </Badge>
                )}
                <Badge className={cn('shrink-0 text-[10px] h-4 px-1', priorityConfig[task.priority].className)}>
                  {priorityConfig[task.priority].label}
                </Badge>
              </div>
            </div>
          </button>
          {canWrite && onTaskEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Edit ${task.title}`}
              title="Edit task"
              data-testid={`task-edit-${task.id}`}
              className="absolute bottom-0 right-0 h-6 w-6 shrink-0 p-0"
              onClick={() => onTaskEdit(task)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
